import { formatDate } from "@/lib/format-data";

import type {
  NoteSupply,
  SupplyLotOption,
  SupplySelection,
} from "@/types/forms/question";
import type { NoteAnswerDetail } from "@/types/notes";

export const MAX_SUPPLY_QUANTITY = 99;

/** The saved draft, or null when the answer has none. */
export function readSupplySelections(
  data: NoteAnswerDetail["data"],
): SupplySelection[] | null {
  const raw = (data as { supplies?: unknown })?.supplies;
  if (!Array.isArray(raw)) return null;
  return raw.flatMap((entry) => {
    if (typeof entry !== "object" || entry === null) return [];
    const { stockId, label, quantity, shipmentId, lotNumber } =
      entry as Partial<SupplySelection>;
    if (typeof stockId !== "string" || !stockId) return [];
    return [
      {
        stockId,
        label: typeof label === "string" ? label : "",
        quantity:
          typeof quantity === "number" && quantity >= 1
            ? Math.min(Math.floor(quantity), MAX_SUPPLY_QUANTITY)
            : 1,
        shipmentId: typeof shipmentId === "string" ? shipmentId : null,
        lotNumber: typeof lotNumber === "string" ? lotNumber : null,
      },
    ];
  });
}

export function selectionsFrom(supplies: NoteSupply[]): SupplySelection[] {
  return supplies.map((s) => ({
    stockId: s.stockId,
    label: s.name,
    quantity: s.quantity,
    shipmentId: s.shipmentId,
    lotNumber: s.lotNumber,
  }));
}

/** "B-204 · Exp Mar 1, 2027 · 5 left"; lots without a number go by received date. */
export function lotLabel(lot: SupplyLotOption): string {
  return [
    lot.lotNumber ?? `Received ${formatDate(lot.receivedDate)}`,
    lot.expiryDate && `Exp ${formatDate(lot.expiryDate)}`,
    `${lot.available} left`,
  ]
    .filter(Boolean)
    .join(" · ");
}

export interface SupplyRowStatus {
  tone: "success" | "muted" | "warning";
  text: string;
}

/** One draft row against what the note has taken from stock. */
export function supplyRowStatus(
  row: SupplySelection,
  taken: NoteSupply | undefined,
): SupplyRowStatus {
  if (!taken) return { tone: "muted", text: "Not taken from stock yet" };
  const parts: string[] = [];
  const diff = row.quantity - taken.quantity;
  if (diff > 0) parts.push(`${taken.quantity} taken · ${diff} more to take`);
  if (diff < 0) parts.push(`${taken.quantity} taken · ${-diff} to put back`);
  if (row.shipmentId !== taken.shipmentId) parts.push("lot change on save");
  if (parts.length > 0) {
    const text = parts.join(" · ");
    return {
      tone: "muted",
      text: text.charAt(0).toUpperCase() + text.slice(1),
    };
  }
  if (taken.drawn < taken.quantity) {
    return {
      tone: "warning",
      text:
        taken.drawn > 0 ? `Only ${taken.drawn} in stock` : "None in stock here",
    };
  }
  return { tone: "success", text: "Taken from stock" };
}

function joinList(items: string[]): string {
  return items.length <= 1
    ? items.join("")
    : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

/** What saving the note will do to stock, or null when the draft matches it. */
export function pendingSummary(
  draft: SupplySelection[],
  taken: NoteSupply[],
): string | null {
  const take: string[] = [];
  const putBack: string[] = [];
  const relot: string[] = [];
  for (const row of draft) {
    const record = taken.find((t) => t.stockId === row.stockId);
    const diff = row.quantity - (record?.quantity ?? 0);
    if (diff > 0) take.push(`${diff} ${row.label}`);
    if (diff < 0) putBack.push(`${-diff} ${row.label}`);
    if (record && diff === 0 && row.shipmentId !== record.shipmentId)
      relot.push(row.label);
  }
  for (const record of taken) {
    if (!draft.some((row) => row.stockId === record.stockId))
      putBack.push(`${record.quantity} ${record.name}`);
  }
  const sentences = [
    take.length > 0 && `Take ${joinList(take)}.`,
    putBack.length > 0 && `Put back ${joinList(putBack)}.`,
    relot.length > 0 && `Switch the lot for ${joinList(relot)}.`,
  ].filter(Boolean);
  return sentences.length > 0 ? sentences.join(" ") : null;
}
